<template>
	<div>
		<div class="stage">
			<div class="row margin-b20">
				<div class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
					<div v-if="selectedReport" class="row mobileMargin-b10">
						<div class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
							<div
								class="grid grid-cols-1 items-center justify-center gap-4 py-4 md:grid-cols-3 md:gap-2"
							>
								<div>
									<button class="jmiBtn blue" @click="clearReport">
										<span class="fa-light fa-file-lines mr-3"></span>Back to reports
									</button>
								</div>
								<h2 class="text-center text-xl font-semibold">
									{{ selectedReport ? `${selectedReport.name}` : 'Please select a report' }}
								</h2>
								<div></div>
							</div>
						</div>
					</div>

					<div class="mx-auto max-w-fit">
						<search-filters
							v-if="selectedReport"
							ref="searchFilters"
							:tenants="tenants"
							:accounts="accounts"
							:zone-names="zoneNames"
							:initial-dates="dates"
							:parent-params="searchParams"
							:carriers="tenantCarriers"
							:search-button-text="'Run Report'"
							:max-date-range="730"
							@apply-filters="handleFilterChange"
							@clear-filters="handleClearFilters"
						/>
					</div>

					<div
						v-if="selectedReport && hasActiveFilters"
						class="row flex-wrap items-center justify-center py-5 pt-2 xs:pb-5 md:pt-4"
					>
						<filter-pills
							:search-params="searchParams"
							:dates="dates"
							:tenants="tenants"
							:accounts="accounts"
							@clear-dates="clearDates"
							@remove-pill="removeFilterPill"
						/>
					</div>

					<div v-if="selectedReport" v-show="loading" class="col-xs-12 col-sm-12 col-md-12 col-lg-12 mt-5">
						<div v-show="loading" class="row margin-b20 center-xs center-md center-lg">
							<div class="loader"></div>
						</div>
					</div>

					<div v-if="!selectedReport">
						<div
							class="mx-auto flex w-full flex-col items-center justify-center gap-4 rounded-md bg-white py-8 dark:bg-[#16191f]"
						>
							<div v-if="loadingReports" class="flex flex-col items-center justify-center gap-4">
								<div class="loader"></div>
								<p class="text-md text-center">Loading available reports...</p>
							</div>
							<div v-else>
								<p class="text-md px-8 text-center">
									You have not selected a report, please make a selection below, or using the report
									selector.
								</p>
								<div class="flex flex-col items-center justify-center gap-8 px-8 py-4">
									<div
										v-for="category in Object.keys(reports)"
										:key="category"
										class="flex w-full max-w-4xl flex-col gap-4"
									>
										<p
											class="border-b-2 border-offwhite pb-2 text-center text-xl font-semibold dark:border-offmain"
										>
											{{ category }}
										</p>
										<div class="flex flex-wrap justify-center gap-6">
											<button
												v-for="report in reports[category]"
												:key="report.id"
												class="jmiBtn flex min-w-[180px] items-center justify-center whitespace-nowrap rounded-lg bg-mainLight px-6 py-3 text-base font-medium normal-case text-white shadow-md transition-all duration-200 hover:bg-main hover:shadow-lg dark:border-[#16191f]"
												@click="setReport(report)"
											>
												<span class="fa-light fa-file-lines mr-2"></span>{{ report.name }}
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div v-if="reportData && reportData.length && !loading" class="flex flex-col">
						<div class="flex items-center justify-between px-4 py-2">
							<div class="flex items-center gap-2">
								<div v-if="reportData?.length" class="flex flex-col gap-6">
									<div class="closeOnMe relative">
										<div class="closeOnMe">
											<button
												ref="columnSelectorButton"
												class="textUppercase textSml closeOnMe group"
												@click="columnSelectorDropdownVisible = !columnSelectorDropdownVisible"
											>
												{{ rowKeys.length - filteredColumns.length }} /
												{{ rowKeys.length }} Columns
												<span class="fa-light fa-chevron-down h-4 w-4"></span>
											</button>
										</div>
										<div
											v-if="columnSelectorDropdownVisible"
											class="closeOnMe absolute z-10 mt-2 max-h-60 w-full overflow-auto rounded-md bg-white shadow-lg"
										>
											<p
												v-if="selectedColumns.length"
												class="sticky left-0 top-0 z-10 bg-light px-2 py-0.5 text-black dark:bg-main dark:text-light"
											>
												Selected
											</p>
											<div
												v-for="(column, i) in selectedColumns"
												:id="'columnSelector-' + i"
												:key="i"
												class="cursor-pointer bg-blue-100 p-2 text-secondary hover:bg-blue-200 dark:bg-secondary dark:text-white dark:hover:bg-secondary/80"
												@click="toggleColumn(column)"
												@mousedown.prevent
												@touchstart.stop
											>
												<input type="checkbox" checked class="mr-2" />{{
													$global.prettyPrint(column)
												}}
											</div>
											<p
												v-if="selectedColumns.length"
												class="sticky left-0 top-0 z-10 bg-light px-2 py-0.5 text-black dark:bg-main dark:text-light"
											>
												Available
											</p>
											<div
												v-for="(column, i) in filteredColumns"
												:id="'columnSelector-available-' + i"
												:key="i"
												class="cursor-pointer bg-white p-2 text-secondary hover:bg-blue-100 dark:bg-main dark:text-white dark:hover:bg-secondary"
												@click="toggleColumn(column)"
												@mousedown.prevent
												@touchstart.stop
											>
												<input type="checkbox" class="mr-2" />{{ $global.prettyPrint(column) }}
											</div>
										</div>
									</div>
								</div>
							</div>
							<div class="flex items-center gap-2">
								<button
									v-if="selectedReport"
									:disabled="!selectedReport"
									class="jmiBtn blue withIcon"
									@click="exportToCsv()"
								>
									<span class="fa-light fa-download"></span>Export
								</button>
							</div>
						</div>
					</div>

					<div
						v-if="reportData && reportData.length && !loading"
						class="hidden flex-wrap px-[15px] py-[10px] sm:flex"
					>
						<div
							v-for="key in selectedColumns"
							:key="key"
							class="min-w-0 flex-1 overflow-hidden overflow-ellipsis whitespace-nowrap px-1 text-left text-xs font-semibold uppercase text-[#999999]"
							:style="{ minWidth: `${Math.max(120, 100 / Math.min(selectedColumns.length, 8))}px` }"
						>
							{{ $global.prettyPrint(key) }}
						</div>
					</div>

					<div
						v-if="selectedReport && (!reportData || !reportData.length || !totalResults)"
						v-show="!loading"
						class="w-full border-b border-t border-gray-200 bg-gray-50 py-6 dark:border-gray-700 dark:bg-[#1c2128]"
						:class="!hasActiveFilters ? 'mt-5' : ''"
					>
						<div
							class="mx-auto max-w-3xl overflow-hidden rounded-lg bg-white p-8 shadow-md dark:bg-[#16191f]"
						>
							<div class="flex flex-col items-center justify-center text-center">
								<div class="mb-4">
									<span v-if="initialLoad" class="fa-light fa-filter text-4xl text-mainLight"></span>
									<span v-else class="fa-light fa-circle-info text-4xl text-mainLight"></span>
								</div>
								<h3 class="mb-2 text-xl font-medium">
									{{ initialLoad ? 'Ready to Run Report' : 'No Results Found' }}
								</h3>
								<p v-if="initialLoad" class="text-gray-600 dark:text-gray-300">
									Please choose your filters and click 'Run Report' to view data
								</p>
								<p v-else class="text-gray-600 dark:text-gray-300">
									No data matches your current filter criteria. Try adjusting your filters.
								</p>
							</div>
						</div>
					</div>

					<div
						v-for="(row, i) in reportData"
						v-show="!loading"
						:key="i"
						class="card listItem !mb-2 cursor-default p-0"
					>
						<div class="block sm:hidden">
							<div
								class="relative rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
							>
								<div class="flex w-full flex-col">
									<div class="flex items-start justify-between">
										<div class="flex-grow">
											<div class="w-full p-4">
												<div class="mb-2 flex items-center justify-between">
													<div class="min-w-0 flex-1">
														<div class="mb-0.5 flex min-w-0 items-center gap-1.5">
															<div
																class="h-2 w-2 flex-shrink-0 rounded-full bg-blue-500"
															></div>
															<p
																class="truncate text-sm font-bold text-gray-900 dark:text-white"
																:title="row[selectedColumns[0]]"
															>
																{{ row[selectedColumns[0]] || 'None' }}
															</p>
														</div>
													</div>
												</div>

												<div class="grid grid-cols-2 gap-2">
													<template v-for="key in selectedColumns.slice(1)" :key="key">
														<div
															class="flex min-w-0 flex-col rounded-md bg-gray-50 px-2 py-1.5 transition-colors duration-200 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600"
														>
															<p
																class="mb-0.5 truncate text-xs font-medium text-gray-500 dark:text-gray-400"
															>
																{{ $global.prettyPrint(key) }}
															</p>
															<p
																class="truncate text-xs font-semibold text-gray-900 dark:text-white"
																:title="row[key]"
															>
																{{ row[key] || 'None' }}
															</p>
														</div>
													</template>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div class="hidden sm:block">
							<div class="flex flex-wrap px-[15px] py-[10px]">
								<div
									v-for="key in selectedColumns"
									:key="key"
									class="min-w-0 flex-1 overflow-hidden overflow-ellipsis whitespace-nowrap px-1 text-left text-xs"
									:style="{
										minWidth: `${Math.max(120, 100 / Math.min(selectedColumns.length, 8))}px`
									}"
									@mouseover="createTooltip($event, row[key])"
								>
									<span class="block text-xs font-semibold uppercase text-gray-500 md:hidden">{{
										key
									}}</span>
									<span>{{ row[key] || 'N/A' }}</span>
								</div>
							</div>
						</div>
					</div>

					<div
						v-if="selectedReport && !loading && reportData && reportData.length && !initialLoad"
						class="flex justify-end"
					>
						<Pagination
							:model-value="pageSize"
							:current-page="currentPage"
							:total-pages="pages"
							:page-sizes="[25, 50, 100]"
							@update:model-value="changePageSize"
							@page-change="changePage"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import reportsApi from '../scripts/reports/reports.api'
import carriersApi from '../scripts/carriers/carriers.api'
import { ref } from 'vue'
import moment from 'moment'
import tariffcodesApi from '@/scripts/tariffcodes/tariffcodes.api'
import accountsApi from '@/scripts/accounts/accounts.api'
import SearchFilters from '@/components/SearchFilters.vue'
import FilterPills from '@/components/FilterPills.vue'
import Pagination from '@/components/common/Pagination.vue'

export default {
	components: {
		SearchFilters,
		FilterPills,
		Pagination
	},

	inject: ['user', 'userRoles', 'tenants'],

	setup() {
		const date = ref(new Date())
		const dp = ref()

		const selectDate = () => {
			dp.value.selectDate()
		}
		const closeMenu = () => {
			dp.value.closeMenu()
		}

		return {
			date,
			dp,
			selectDate,
			closeMenu
		}
	},

	data() {
		return {
			showCharts: true,
			error: '',
			dates: [moment().startOf('month').toDate(), null],
			reportData: {},
			totalResults: 0,
			allSelected: false,
			pageSize: 25,
			currentPage: 1,
			accounts: [],
			totalALegAttempts: 0,
			totalALegConnected: 0,
			totalBLegAttempts: 0,
			totalBLegConnected: 0,
			reportMenu: false,
			carriers: [],
			loading: false,
			reports: {},
			selectedReport: '',
			searchParams: {
				dateAddedFrom: moment().startOf('month').format('YYYY-MM-DD'),
				dateAddedTo: null,
				selectedTenant: null,
				selectedAccount: null,
				carrier: null,
				carrierName: null,
				callDurationFrom: '',
				callDurationTo: '',
				billableDurationFrom: '',
				billableDurationTo: '',
				callFrom: '',
				callTo: '',
				zoneNames: [],
				hangupCodes: []
			},
			screenWidth: 1024,
			columnSelectorDropdownVisible: false,
			zoneNames: [],
			initialLoad: true,
			selectedColumns: [],
			hover: null,
			isInitialisingSearchParams: true,
			presetActive: false,
			activePreset: null,
			loadingReports: true
		}
	},

	computed: {
		tenantCarriers() {
			return this.carriers
				.filter(
					(c) => !this.searchParams?.selectedTenant || c.tenant?.id === this.searchParams.selectedTenant.id
				)
				.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
		},
		tenantAccounts() {
			return this.accounts
				.filter(
					(a) => !this.searchParams?.selectedTenant || a.tenant?.id === this.searchParams.selectedTenant.id
				)
				.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
		},
		pages() {
			let numPages = Math.ceil(this.totalResults / this.pageSize) || 1
			return Array.apply(null, { length: numPages }).map(Number.call, Number)
		},
		rowKeys() {
			if (this.reportData && this.reportData.length && this.reportData[0]) {
				return Object.keys(this.reportData[0]).filter((key) => key !== 'Tenant' || this.tenants?.length > 1)
			}
			return []
		},
		zonePlaceholderText() {
			if (this.selectedZoneNames.length) {
				return this.selectedZoneNames.join(', ')
			} else {
				return 'Search for zones'
			}
		},
		hangupCodePlaceholderText() {
			if (this.selectedHangupCodes.length) {
				return this.selectedHangupCodes.map((h) => `${h.code}`).join(', ')
			} else {
				return 'Search for hangup codes'
			}
		},
		filteredZoneNames() {
			let searchZoneLower = this.searchZone.toLowerCase()
			return searchZoneLower
				? this.zoneNames.filter(
						(zone) => !this.selectedZoneNames.includes(zone) && zone.toLowerCase().includes(searchZoneLower)
					)
				: this.zoneNames.filter((zone) => !this.selectedZoneNames.includes(zone))
		},
		filteredHangupCodes() {
			let allHangupCodes = []
			Object.keys(this.statusCodes).forEach((key) => {
				if (key !== 'Common') allHangupCodes = allHangupCodes.concat(this.statusCodes[key])
			})
			return this.searchHangupCode
				? allHangupCodes.filter(
						(h) =>
							!this.selectedHangupCodes.includes(h) && h.code.toString().includes(this.searchHangupCode)
					)
				: allHangupCodes.filter((h) => !this.selectedHangupCodes.includes(h))
		},
		filteredColumns() {
			return this.selectedColumns.length
				? Object.keys(this.reportData[0])
						.filter((key) => !this.selectedColumns.includes(key))
						.filter((key) => key !== 'Tenant' || this.tenants?.length > 1)
				: Object.keys(this.reportData[0]).filter((key) => key !== 'Tenant' || this.tenants?.length > 1)
		},
		hasActiveFilters() {
			return (
				this.searchParams.dateAddedFrom ||
				this.searchParams.dateAddedTo ||
				(this.searchParams.selectedTenant && Object.keys(this.searchParams.selectedTenant).length) ||
				(this.searchParams.selectedAccount && Object.keys(this.searchParams.selectedAccount).length) ||
				this.searchParams.carrier ||
				this.searchParams.callDurationFrom ||
				this.searchParams.callDurationTo ||
				this.searchParams.billableDurationFrom ||
				this.searchParams.billableDurationTo ||
				(this.searchParams.zoneNames && this.searchParams.zoneNames.length > 0) ||
				this.searchParams.callFrom ||
				this.searchParams.callTo ||
				this.searchParams.callAnywhere ||
				(this.searchParams.hangupCodes && this.searchParams.hangupCodes.length > 0)
			)
		}
	},

	watch: {
		dates: {
			handler: function (val) {
				if (this.isInitialisingSearchParams) return

				if (val?.length === 2) {
					if (val[0]) this.searchParams.dateAddedFrom = moment(val[0]).format('YYYY-MM-DD')
					else this.searchParams.dateAddedFrom = null

					if (val[1]) this.searchParams.dateAddedTo = moment(val[1]).format('YYYY-MM-DD')
					else this.searchParams.dateAddedTo = null

					if (this.presetActive) {
						this.removePresetFromUrl()
						this.presetActive = false
						this.activePreset = null
					}
					this.updateUrlParams()
				}
			},
			deep: true
		},
		selectedReport: {
			async handler(newValue, oldValue) {
				if (newValue) {
					this.selectedColumns = []
				}
			}
		},
		screenWidth: {
			handler(newValue, oldValue) {
				this.checkScreenSize(newValue)
			}
		},
		searchParams: {
			deep: true,
			handler(newValue, oldValue) {
				if (this.isInitialisingSearchParams) {
					this.isInitialisingSearchParams = false
					return
				}
			}
		}
	},

	async created() {
		this.loading = true

		try {
			const query = this.$route.query

			let presetActive = false
			if (query.preset) {
				this.activePreset = query.preset
				let start, end
				const now = new Date()
				if (query.preset === 'last24h') {
					end = now
					start = new Date(now.getTime() - 24 * 60 * 60 * 1000)
				} else if (query.preset === 'lastWeek') {
					end = now
					start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
				} else if (query.preset === 'last30d') {
					end = now
					start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
				} else if (query.preset === 'thisMonth') {
					start = new Date(now.getFullYear(), now.getMonth(), 1)
					end = now
				} else if (query.preset === 'lastMonth') {
					start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
					end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999)
				}
				if (start && end) {
					this.dates = [start, end]
					presetActive = true
				}
			}

			if (!this.userRoles?.includes('account')) {
				const [carriersResponse, accountsResponse] = await Promise.all([
					carriersApi.getAll(),
					accountsApi.getAll({ limit: 2500 })
				])
				this.carriers = carriersResponse.data.results
				this.accounts = accountsResponse.data.results
			}

			const response = await reportsApi.getAll()
			response.data.forEach((report) => {
				this.reports[report.category] = this.reports[report.category] || []
				this.reports[report.category].push(report)
			})
			this.loadingReports = false

			const zoneResponse = await tariffcodesApi.getAllZoneNames()
			this.zoneNames = zoneResponse.data?.results || []

			let selectedReport = null
			if (query.report) {
				const allReports = Object.values(this.reports).flat()
				const report = allReports.find((r) => r.id === query.report || r.id === parseInt(query.report))
				if (report) {
					selectedReport = report
					this.selectedReport = report
					this.initialLoad = false
				}
			}

			let selectedTenant = null
			let selectedAccount = null

			if (query.tenant) {
				const tenant = this.tenants.find((t) => t.id === parseInt(query.tenant))
				if (tenant) {
					selectedTenant = tenant
				}
			}

			if (query.account && this.accounts.length > 0) {
				const account = this.accounts.find((a) => a.id === parseInt(query.account))
				if (account) {
					selectedAccount = account
				}
			}

			let dateFrom = null
			let dateTo = null

			if (presetActive) {
				dateFrom = null
				dateTo = null
			} else if (query.dateFrom || query.dateTo) {
				dateFrom = query.dateFrom
				dateTo = query.dateTo
				this.dates = [
					query.dateFrom ? new Date(query.dateFrom) : null,
					query.dateTo ? new Date(query.dateTo) : null
				].filter(Boolean)
				presetActive = false
			} else if (!query.report && Object.keys(query).length === 0) {
				dateFrom = moment().startOf('month').format('YYYY-MM-DD')
				this.dates = [moment().startOf('month').toDate(), null]
			}

			this.searchParams = {
				dateAddedFrom: dateFrom,
				dateAddedTo: dateTo,
				selectedTenant: selectedTenant,
				selectedAccount: selectedAccount,
				carrier: null,
				carrierName: null,
				callDurationFrom: query.durationFrom ? parseInt(query.durationFrom) : null,
				callDurationTo: query.durationTo ? parseInt(query.durationTo) : null,
				billableDurationFrom: query.billableDurationFrom ? parseInt(query.billableDurationFrom) : null,
				billableDurationTo: query.billableDurationTo ? parseInt(query.billableDurationTo) : null,
				callFrom: query.callFrom || '',
				callTo: query.callTo || '',
				callAnywhere: query.callAnywhere || '',
				zoneNames: query.zones ? query.zones.split(',').map((zone) => decodeURIComponent(zone)) : [],
				hangupCodes: query.hangupCodes ? query.hangupCodes.split(',').map((code) => parseInt(code)) : [],
				selectedReport: selectedReport
			}

			if (query.carrier) {
				const carrierId = query.carrier
				let carrierName = query.carrierName || ''

				const carrierObj = this.carriers.find((c) => c.id.toString() === carrierId.toString())

				if (carrierObj && !carrierName) {
					carrierName = carrierObj.name
				}

				this.searchParams.carrier = carrierId
				this.searchParams.carrierName = carrierName
			}

			await this.$nextTick()

			if (Object.keys(query).length > 0 && !query.report) {
				this.reloadAll()
			}

			this.isInitialisingSearchParams = false
			this.presetActive = presetActive

			if (presetActive && (query.dateFrom || query.dateTo)) {
				this.updateUrlParams()
			}

			this.loading = false
		} catch (error) {
			console.error('Initialisation error:', error)
			this.isInitialisingSearchParams = false
			this.loadingReports = false
			this.loading = false
		}
	},
	mounted() {
		window.addEventListener('resize', this.handleResize)
		document.addEventListener('click', this.handleClickOutside)
	},

	beforeUnmount() {
		window.removeEventListener('resize', this.handleResize)
		document.removeEventListener('click', this.handleClickOutside)
	},

	methods: {
		handleFilterChange(filters) {
			const modifiedFilters = { ...filters }

			if (
				modifiedFilters.selectedTenant &&
				(!this.searchParams.selectedTenant ||
					modifiedFilters.selectedTenant.id !== this.searchParams.selectedTenant.id)
			) {
				modifiedFilters.carrier = null
				modifiedFilters.carrierName = null
			}

			if (
				modifiedFilters.carrier &&
				typeof modifiedFilters.carrier === 'object' &&
				modifiedFilters.carrier !== null
			) {
				const carrierName = modifiedFilters.carrierName || modifiedFilters.carrier.name || ''

				const carrierId = modifiedFilters.carrier.id

				modifiedFilters.carrier = carrierId
				modifiedFilters.carrierName = carrierName
			}

			if (modifiedFilters.selectedTenant && !modifiedFilters.selectedTenant.companyName) {
				const tenant = this.tenants.find((t) => t.id === modifiedFilters.selectedTenant.id)
				if (tenant) {
					modifiedFilters.selectedTenant = {
						...modifiedFilters.selectedTenant,
						companyName: tenant.companyName
					}
				}
			}

			if (modifiedFilters.selectedAccount && !modifiedFilters.selectedAccount.name) {
				const account = this.accounts.find((a) => a.id === modifiedFilters.selectedAccount.id)
				if (account) {
					modifiedFilters.selectedAccount = {
						...modifiedFilters.selectedAccount,
						name: account.name
					}
				}
			}

			if (modifiedFilters.preset) {
				this.activePreset = modifiedFilters.preset
				this.presetActive = true
			}

			this.searchParams = { ...this.searchParams, ...modifiedFilters }

			this.updateUrlParams()

			if (filters.isSearching) {
				this.initialLoad = false
				this.reloadAll()
			}
		},

		removeFilterPill(pill) {
			switch (pill.category.toLowerCase()) {
				case 'report':
					this.selectedReport = null
					this.searchParams.selectedReport = null
					break
				case 'date':
					this.dates = []
					this.searchParams.dateAddedFrom = null
					this.searchParams.dateAddedTo = null
					if (this.presetActive) {
						this.presetActive = false
						this.activePreset = null
					}
					break
				case 'tenant':
					this.searchParams.selectedTenant = null
					this.searchParams.selectedAccount = null
					break
				case 'account':
					this.searchParams.selectedAccount = null
					break
				case 'carrier':
					this.searchParams.carrier = null
					this.searchParams.carrierName = null
					break
				case 'duration':
					this.searchParams.callDurationFrom = null
					this.searchParams.callDurationTo = null
					break
				case 'billableduration':
				case 'billable duration':
					this.searchParams.billableDurationFrom = null
					this.searchParams.billableDurationTo = null
					break
				case 'zone':
					this.searchParams.zoneNames = []
					break
				case 'source':
					this.searchParams.callFrom = ''
					break
				case 'destination':
					this.searchParams.callTo = ''
					break
				case 'callanywhere':
				case 'call anywhere':
					this.searchParams.callAnywhere = ''
					break
				case 'hangupcode':
				case 'hangup code':
					this.searchParams.hangupCodes = []
					break
			}

			this.updateUrlParams()

			const searchFilters = this.$refs.searchFilters
			if (searchFilters) {
				searchFilters.initialiseFromParams(this.searchParams)
			}

			this.initialLoad = true
		},

		clearDates() {
			this.dates = []
			this.searchParams.dateAddedFrom = null
			this.searchParams.dateAddedTo = null

			if (this.presetActive) {
				this.presetActive = false
				this.activePreset = null
			}

			const searchFilters = this.$refs.searchFilters
			if (searchFilters) {
				searchFilters.clearCurrentFilter('dates')
			}

			this.updateUrlParams()

			this.initialLoad = true
		},

		clearDuration() {
			this.searchParams.callDurationFrom = null
			this.searchParams.callDurationTo = null
			const searchFilters = this.$refs.searchFilters
			if (searchFilters) {
				searchFilters.clearCurrentFilter('duration')
			}

			this.updateUrlParams()

			this.initialLoad = true
		},

		clearCallDetails() {
			this.searchParams.callFrom = ''
			this.searchParams.callTo = ''
			const searchFilters = this.$refs.searchFilters
			if (searchFilters) {
				searchFilters.clearCurrentFilter('call')
			}

			this.updateUrlParams()

			this.initialLoad = true
		},

		handleClearFilters() {
			const currentDateFrom = this.searchParams.dateAddedFrom
			const currentDateTo = this.searchParams.dateAddedTo
			const currentDates = [...this.dates]

			this.searchParams = {
				dateAddedFrom: currentDateFrom,
				dateAddedTo: currentDateTo,
				selectedTenant: null,
				selectedAccount: null,
				carrier: null,
				carrierName: null,
				callDurationFrom: null,
				callDurationTo: null,
				billableDurationFrom: null,
				billableDurationTo: null,
				callFrom: '',
				callTo: '',
				callAnywhere: '',
				zoneNames: [],
				hangupCodes: []
			}

			this.dates = currentDates

			const searchFilters = this.$refs.searchFilters
			if (searchFilters) {
				searchFilters.initialiseFromParams(this.searchParams)
			}

			const query = { ...this.$route.query }

			Object.keys(query).forEach((key) => {
				if (key !== 'report' && key !== 'dateFrom' && key !== 'dateTo' && key !== 'preset') {
					delete query[key]
				}
			})

			if (currentDateFrom) {
				query.dateFrom = currentDateFrom
			}
			if (currentDateTo) {
				query.dateTo = currentDateTo
			}

			if (this.activePreset) {
				query.preset = this.activePreset
			}

			this.$router.replace({ query }).catch(() => {})

			this.initialLoad = true

			this.reportData = []
			this.totalResults = 0
		},

		clearReport() {
			this.selectedReport = null
			this.searchParams = {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: null,
				selectedAccount: null,
				carrier: null,
				carrierName: null,
				callDurationFrom: null,
				callDurationTo: null,
				billableDurationFrom: null,
				billableDurationTo: null,
				callFrom: '',
				callTo: '',
				callAnywhere: '',
				zoneNames: [],
				hangupCodes: []
			}
			this.reportData = []
			this.totalResults = 0
			this.selectedColumns = []
			this.dates = []
			this.initialLoad = true
			this.presetActive = false
			this.activePreset = null

			this.$router.replace({ query: {} }).catch(() => {})

			const searchFilters = this.$refs.searchFilters
			if (searchFilters) {
				searchFilters.clearFilters()
			}
		},

		setTenant($event) {
			if ($event) {
				this.searchParams.selectedTenant = {
					id: $event.id,
					companyName: $event.companyName
				}
			} else {
				this.searchParams.selectedTenant = null
			}
			this.searchParams.selectedAccount = null
			this.searchParams.carrier = null
			this.searchParams.carrierName = null

			this.updateUrlParams()
		},
		checkScreenSize(width) {
			this.screenWidth = width
		},
		setAdvancedParams() {
			this.searchParams.callDurationFrom = parseInt(this.durationMinFilter) || null
			this.searchParams.callDurationTo = parseInt(this.durationMaxFilter) || null
			this.searchParams.billableDurationFrom = parseInt(this.billableMinFilter) || null
			this.searchParams.billableDurationTo = parseInt(this.billableMaxFilter) || null
			this.searchParams.callFrom = this.searchSource
			this.searchParams.callTo = this.searchDest
			this.searchParams.zoneNames = this.selectedZoneNames.map((z) => z)
			this.searchParams.hangupCodes = this.selectedHangupCodes.map((h) => h.code)
			this.reloadReport()
		},
		availableHangupCodes(codeGroup) {
			return this.filteredHangupCodes.filter((h) => this.statusCodes[codeGroup].find((cg) => cg.code === h.code))
		},
		log(txt) {
			console.log(txt)
			return true
		},
		reloadAll() {
			this.reloadReport()
			this.reloadStats()
		},
		toggleAdvancedFilters() {
			this.advancedFiltersVisible = !this.advancedFiltersVisible
		},
		resetSearchParams() {
			this.searchParams = {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: '',
				selectedAccount: '',
				carrier: '',
				callDurationFrom: '',
				callDurationTo: '',
				billableDurationFrom: '',
				billableDurationTo: '',
				hangupCodes: [],
				callFrom: '',
				callTo: '',
				zoneNames: []
			}
			this.dates = []
			this.selectedHangupCodes = []
			this.selectedZoneNames = []
			this.searchSource = ''
			this.searchDest = ''
			this.durationMinFilter = ''
			this.durationMaxFilter = ''
			this.billableMinFilter = ''
			this.billableMaxFilter = ''

			this.reloadAll()
		},
		getSearchParams() {
			let params = {
				limit: this.pageSize,
				skip: this.pageSize * (this.currentPage - 1)
			}

			if (this.searchParams) {
				if (this.searchParams.dateAddedFrom) {
					params.dateAddedFrom = moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD')
				}

				if (this.searchParams.dateAddedTo) {
					params.dateAddedTo = moment(this.searchParams.dateAddedTo).format('YYYY-MM-DD')
				}

				if (this.searchParams.selectedTenant) {
					params.selectedTenant = this.searchParams.selectedTenant
				}

				if (this.searchParams.selectedAccount) {
					params.selectedAccount = this.searchParams.selectedAccount
				}

				if (this.searchParams.carrier) {
					if (typeof this.searchParams.carrier === 'object' && this.searchParams.carrier !== null) {
						params.carrier = this.searchParams.carrier.id
					} else {
						params.carrier = this.searchParams.carrier
					}

					params.carrierName = this.searchParams.carrierName || ''
				}

				if (this.searchParams.callDurationFrom) {
					params.callDurationFrom = this.searchParams.callDurationFrom
				}

				if (this.searchParams.callDurationTo) {
					params.callDurationTo = this.searchParams.callDurationTo
				}

				if (this.searchParams.billableDurationFrom) {
					params.billableDurationFrom = this.searchParams.billableDurationFrom
				}

				if (this.searchParams.billableDurationTo) {
					params.billableDurationTo = this.searchParams.billableDurationTo
				}

				if (this.searchParams.callFrom) {
					params.callFrom = this.searchParams.callFrom
				}

				if (this.searchParams.callTo) {
					params.callTo = this.searchParams.callTo
				}

				if (this.searchParams.callAnywhere) {
					params.callAnywhere = this.searchParams.callAnywhere
				}

				if (this.searchParams.zoneNames && this.searchParams.zoneNames.length) {
					params.zoneNames = this.searchParams.zoneNames
				}

				if (this.searchParams.hangupCodes && this.searchParams.hangupCodes.length) {
					params.hangupCodes = this.searchParams.hangupCodes
				}
			}

			return params
		},
		hideTenantDropdown() {
			this.tenantDropdownVisible = false
		},
		hideZoneDropdown() {
			this.zoneDropdownVisible = false
			this.searchZone = ''
		},
		hideHangupCodeDropdown() {
			this.hangupCodeDropdownVisible = false
			this.searchHangupCode = ''
		},
		toggleZoneSelection(zoneName) {
			const index = this.selectedZoneNames.indexOf(zoneName)
			if (index >= 0) {
				this.selectedZoneNames.splice(index, 1)
			} else {
				this.selectedZoneNames.push(zoneName)
			}
		},
		toggleColumn(column) {
			const index = this.selectedColumns.indexOf(column)
			if (index >= 0) {
				this.selectedColumns.splice(index, 1)
			} else {
				this.selectedColumns.push(column)
			}
		},
		toggleHangupCodeSelection(hangupCode) {
			const index = this.selectedHangupCodes.indexOf(hangupCode)
			if (index >= 0) {
				this.selectedHangupCodes.splice(index, 1)
			} else {
				this.selectedHangupCodes.push(hangupCode)
			}
		},
		toggleReportMenu() {
			if (this.reportMenu) {
				this.reportMenu = false
			} else {
				this.reportMenu = true
			}
		},
		handleClickOutside(event) {
			if (this.$refs.reportSelectorButton && !this.$refs.reportSelectorButton.contains(event.target)) {
				this.reportMenu = false
			}
			if (
				this.$refs.columnSelectorButton &&
				!this.$refs.columnSelectorButton.contains(event.target) &&
				!event.target.id.startsWith('columnSelector')
			) {
				this.columnSelectorDropdownVisible = false
			}
		},
		changePage(page) {
			this.currentPage = page
			this.reloadReport()
		},
		changePageSize(size) {
			this.pageSize = size
			let numPages = Math.ceil(this.totalResults / size) || 1

			if (numPages < this.currentPage) this.currentPage = numPages

			this.reloadReport()
		},
		setReport(report) {
			this.selectedReport = report
			this.reportMenu = false
			this.currentPage = 1
			this.initialLoad = true
			this.reportData = []
			this.totalResults = 0

			const startOfMonth = moment().startOf('month').toDate()
			this.dates = [startOfMonth, null]

			const currentTenant = this.searchParams.selectedTenant
			const currentAccount = this.searchParams.selectedAccount

			this.searchParams = {
				...this.searchParams,
				dateAddedFrom: moment(startOfMonth).format('YYYY-MM-DD'),
				dateAddedTo: null,
				selectedReport: report,
				selectedTenant: currentTenant,
				selectedAccount: currentAccount
			}

			const query = {
				...this.$route.query,
				report: report.id,
				dateFrom: this.searchParams.dateAddedFrom,
				dateTo: this.searchParams.dateAddedTo
			}

			if (currentTenant?.id) {
				query.tenant = currentTenant.id
			}
			if (currentAccount?.id) {
				query.account = currentAccount.id
			}

			this.$router.replace({ query }).catch(() => {})

			this.$nextTick(() => {
				const searchFilters = this.$refs.searchFilters
				if (searchFilters) {
					searchFilters.initialiseFromParams(this.searchParams)
				}
			})
		},
		routeTo(routeName) {
			this.$router.push(routeName)
		},
		toggleSelectAllCalls() {
			this.allSelected = !this.allSelected

			for (let i = 0; i < this.contacts.length; i++) {
				this.contacts[i].selected = this.allSelected
			}
		},
		exportToCsv() {
			if (this.selectedReport) {
				let params = this.getSearchParams()

				reportsApi
					.runReportAsCsv(this.selectedReport.id, params)
					.then((response) => {
						let blob = new Blob([response.data], { type: 'text/csv' })
						let link = document.createElement('a')
						link.href = window.URL.createObjectURL(blob)
						link.download = `${this.selectedReport.name}.csv`
						link.click()
					})
					.catch((err) => {
						console.error(err)
					})
			} else {
				this.reportData = {}
				this.loading = false
			}
		},
		async reloadReport() {
			if (this.selectedReport) {
				this.loading = true
				this.initialLoad = false

				let params = this.getSearchParams()

				try {
					const response = await reportsApi.runReport(this.selectedReport.id, params)
					if (response.data) {
						this.reportData = response.data.results || []
						if (this.reportData.length && !this.selectedColumns?.length) {
							const keys = Object.keys(this.reportData[0])
							keys.slice(0, 10).forEach((key) => this.toggleColumn(key))
						}
						this.totalResults = response.data.count

						const maxPages = Math.ceil(this.totalResults / this.pageSize) || 1
						if (this.currentPage > maxPages) {
							this.currentPage = 1
							this.reloadReport()
							return
						}

						this.screenWidth = window.innerWidth
					}
					this.loading = false
				} catch (err) {
					console.error('API error:', err)
					this.totalResults = 0
					this.reportData = []
					this.loading = false
				}
			} else {
				this.reportData = []
				this.loading = false
			}
		},
		reloadStats() {
			let params = this.getSearchParams()

			reportsApi
				.getStats(params)
				.then((response) => {
					if (response.data && response.data[0]) {
						this.totalALegAttempts = parseInt(response.data[0].totalALegAttempts) || 0
						this.totalALegConnected = parseInt(response.data[0].totalALegConnected) || 0
						this.totalBLegAttempts = parseInt(response.data[0].totalBLegAttempts) || 0
						this.totalBLegConnected = parseInt(response.data[0].totalBLegConnected) || 0
					} else {
						this.totalALegAttempts = 0
						this.totalALegConnected = 0
						this.totalBLegAttempts = 0
						this.totalBLegConnected = 0
					}
				})
				.catch((err) => {
					console.error(err)
				})
		},

		handleResize() {
			this.screenWidth = window.innerWidth
		},
		close() {
			if (this.reportMenu) {
				this.reportMenu = true
			}
		},
		updateUrlParams() {
			const params = new URLSearchParams()

			if (this.selectedReport?.id) {
				params.set('report', this.selectedReport.id)
			}

			if (!this.presetActive) {
				if (this.searchParams.dateAddedFrom) {
					params.set('dateFrom', this.searchParams.dateAddedFrom)
				}
				if (this.searchParams.dateAddedTo) {
					params.set('dateTo', this.searchParams.dateAddedTo)
				}
			}

			if (this.searchParams.selectedTenant?.id) {
				params.set('tenant', this.searchParams.selectedTenant.id)
			}
			if (this.searchParams.selectedAccount?.id) {
				params.set('account', this.searchParams.selectedAccount.id)
			}
			if (this.searchParams.carrier) {
				if (typeof this.searchParams.carrier === 'object' && this.searchParams.carrier !== null) {
					params.set('carrier', this.searchParams.carrier.id)
				} else {
					params.set('carrier', this.searchParams.carrier)
				}

				params.set('carrierName', this.searchParams.carrierName || '')
			}
			if (this.searchParams.callDurationFrom) {
				params.set('durationFrom', this.searchParams.callDurationFrom)
			}
			if (this.searchParams.callDurationTo) {
				params.set('durationTo', this.searchParams.callDurationTo)
			}
			if (this.searchParams.billableDurationFrom) {
				params.set('billableDurationFrom', this.searchParams.billableDurationFrom)
			}
			if (this.searchParams.billableDurationTo) {
				params.set('billableDurationTo', this.searchParams.billableDurationTo)
			}
			if (this.searchParams.callFrom) {
				params.set('callFrom', this.searchParams.callFrom)
			}
			if (this.searchParams.callTo) {
				params.set('callTo', this.searchParams.callTo)
			}
			if (this.searchParams.callAnywhere) {
				params.set('callAnywhere', this.searchParams.callAnywhere)
			}
			if (this.searchParams.hangupCodes?.length) {
				params.set('hangupCodes', this.searchParams.hangupCodes.join(','))
			}
			if (this.searchParams.zoneNames?.length) {
				params.set('zones', this.searchParams.zoneNames.join(','))
			}

			if (this.activePreset) {
				params.set('preset', this.activePreset)
			}

			const query = {}
			for (const [key, value] of params.entries()) {
				query[key] = value
			}

			this.$router.replace({ query }).catch(() => {})
		},
		removePresetFromUrl() {
			const url = new URL(window.location.href)
			url.searchParams.delete('preset')
			this.$router.replace({ query: Object.fromEntries(url.searchParams) }).catch(() => {})
		},
		createTooltip(event, text) {
			if (text) {
				event.target.setAttribute('title', text)
			}
		}
	}
}
</script>
